import React, { useCallback, useMemo, useState } from 'react';

import { List, ListItem, ListItemText, Menu, MenuItem } from '@mui/material';
import { styled, Box, UnreadIndicator, IconButton, Icon } from '@liscio/ui';
import { useNavigate } from 'react-router-dom';

import { BasicMessage } from '@liscio/api';
import { getScrollValue } from '@liscio/utils';

import { Primary } from './components/Primary/Primary';
import { useProfileData } from '../../fetch-utils/users/user-hooks';
import {
  useToggleThreadIsPinned,
  useUpdateMessageUnreadStatus,
} from '../../fetch-utils/messages/messages-hooks';
import { ListItemButton, Avatar, ListItemAvatar } from '../index';
import {
  MESSAGES_DOMAIN,
  MESSAGES_PATHS,
} from '../../modules/messages/route-constants';

export interface MessagesListProps {
  messages: BasicMessage[];
  // Optional state to pass to navigate options
  navigateState?: Record<string, any>;
  // Whether or not to display unread messages dot
  highlightUnreadComments?: boolean;
  showDashboardMetadata?: boolean;
}

const IndicatorWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '22px',
  minHeight: '70px',
});

export const MessagesList = ({
  messages,
  highlightUnreadComments = false,
  navigateState = {},
  showDashboardMetadata = false,
}: MessagesListProps) => {
  const navigate = useNavigate();
  const { cpa_user_id } = useProfileData();
  const { mutateAsync: markUnread } = useUpdateMessageUnreadStatus();
  const { mutateAsync: toggleThreadIsPinned } = useToggleThreadIsPinned();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMessageId, setMenuMessageId] = useState<string | null>(null);
  const [menuThreadId, setMenuThreadId] = useState<string | null>(null);
  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const onMessageClick = useCallback(
    (message: BasicMessage) => {
      // Navigate to thread details - the wrapper component will determine
      // whether to show new message form (for new message drafts) or
      // thread details view (for reply drafts and sent messages)
      navigate(
        `/${MESSAGES_DOMAIN}${MESSAGES_PATHS.details}/${encodeURIComponent(
          message.thread_id
        )}`,
        {
          state: {
            scroll: getScrollValue(),
            ...navigateState,
          },
        }
      );
    },
    [navigate, navigateState]
  );
  const openMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    messageId: string,
    threadId: string
  ) => {
    event.stopPropagation();
    setMenuMessageId(messageId);
    setMenuThreadId(threadId);
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
    setMenuMessageId(null);
    setMenuThreadId(null);
  };

  const onMarkUnread = async () => {
    if (!menuMessageId || !cpa_user_id) return closeMenu();
    await markUnread({ cpa_user_id, message_id: menuMessageId });
    closeMenu();
  };

  const onTogglePin = async () => {
    if (!menuThreadId) return closeMenu();
    await toggleThreadIsPinned(menuThreadId);
    closeMenu();
  };

  const onCreateTask = () => {
    navigate('/tasks/list', {
      state: { source: 'messages', threadId: menuThreadId },
    });
    closeMenu();
  };

  const onCreateNote = () => {
    navigate('/note/new', {
      state: { source: 'messages', threadId: menuThreadId },
    });
    closeMenu();
  };
  return (
    <>
      <List>
        {messages.map((message) => {
          const shouldDisplayAsUnread =
            highlightUnreadComments && !message.read;

          return (
            <ListItem key={message.thread_id} disablePadding>
              <ListItemButton onClick={() => onMessageClick(message)}>
                <IndicatorWrapper>
                  <UnreadIndicator isUnread={shouldDisplayAsUnread} />
                </IndicatorWrapper>
                <ListItemAvatar>
                  <Avatar src={message.avatar} alt={message.sender}>
                    {message.sender
                      ?.trim()
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((part) => part[0])
                      .join('')
                      .toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Primary
                      message={message}
                      shouldDisplayAsUnread={shouldDisplayAsUnread}
                      showDashboardMetadata={showDashboardMetadata}
                    />
                  }
                />
                <IconButton
                  aria-label={`More actions for thread ${message.thread_id}`}
                  size="medium"
                  onClick={(e) => openMenu(e, message.id, message.thread_id)}
                >
                  <Icon icon="ellipsis" fontSize="medium" />
                </IconButton>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        keepMounted
      >
        <MenuItem onClick={onMarkUnread}>Mark as Unread</MenuItem>
        <MenuItem onClick={onTogglePin}>Toggle Pin</MenuItem>
        <MenuItem onClick={onCreateTask}>Create Task</MenuItem>
        <MenuItem onClick={onCreateNote}>Create Note</MenuItem>
      </Menu>
    </>
  );
};

export default MessagesList;
